import { Label } from '@ekkogmbh/apisdk';
import { ColumnDef, DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import { ContentControl } from 'src/Common/Components/ContentControl';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import DeviceStatus from './DeviceStatus';
import { Highlight } from '@mui/icons-material';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useCallback } from 'react';

// Shared row height across all scrollable columns so scroll indicators
// align at the same vertical position in each row.
const ROW_MIN_HEIGHT = 48;

export const LabelManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const [isBlinkRequesting, startBlinkRequest] = useRequest<void>();

  const fetchLabels = useCallback((pagination) => api.getLabels(pagination), []);
  const blink = useCallback((label: Label) => {
    startBlinkRequest(() => api.blinkLabel(label));
  }, []);

  const columns: ColumnDef[] = [
    {
      header: 'Id',
      body: (entry: Label) => entry.id,
      filterField: 'id',
      sortField: 'L.id',
      fitContent: true,
    },
    {
      header: 'Technology',
      body: (entry: Label) => entry.technology,
      filterField: 'technology',
      sortField: 'L.technology',
      fitContent: true,
    },
    {
      header: 'Coordinate',
      body: (entry: Label, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(entry.identifierValue ?? '', [searchText]) : entry.identifierValue}
        </ScrollableStack>
      ),
      filterField: 'identifier',
      sortField: 'Node.materializedPath',
      proportionalContent: '40%',
    },
    {
      header: 'Status',
      body: (entry: Label) => <DeviceStatus value={entry.status} />,
      filterField: 'status',
      sortField: 'L.status',
      fitContent: true,
    },
    {
      header: 'Battery-Status',
      body: (entry: Label) => <DeviceStatus value={entry.batteryStatus} />,
      filterField: 'batteryStatus',
      sortField: 'L.batteryStatus',
      fitContent: true,
    },
  ];

  return (
    <ContentControl>
      <DataTable
        fetchItems={fetchLabels}
        actions={[
          {
            title: 'Blink',
            icon: <Highlight />,
            onClick: (entry: Label) => blink(entry),
            disabled: isBlinkRequesting,
          },
        ]}
        columns={columns}
      />
    </ContentControl>
  );
};
